<template>
    <v-container fluid>
        <v-card elevation="2" class="pa-0">
            <v-card-title class="d-flex justify-space-between">
                Gestione Cashflow Previsionale
                <div class="d-flex gap-2">
                    <v-btn color="success" @click="showCharts = !showCharts" outlined class="mr-2">
                        <v-icon left>{{ showCharts ? 'table_chart' : 'show_chart' }}</v-icon>
                        {{ showCharts ? 'Tabella' : 'Grafici' }}
                    </v-btn>
                    <v-btn color="info" @click="exportToExcel" outlined :loading="exporting" class="mr-2">
                        <v-icon left>download</v-icon> Esporta Excel
                    </v-btn>
                </div>
                <div class="d-flex gap-2 pt-1">
                    <v-btn color=" primary" @click="openDialog">
                        <v-icon left>add</v-icon> Nuovo Record
                    </v-btn>
                </div>
            </v-card-title>

            <CashflowSettings :starting-balance.sync="startingBalance" :starting-date.sync="startingDate"
                @refresh="fetchData" @importaDati="importaDati" :message="message" :history-date.sync="historyDate"
                :history.sync="history" :modified="impostazioniModificate" :saving="savingSettings"
                @salvaImpostazioni="salvaImpostazioni" />

            <CashflowCharts v-if="showCharts" :items="calculatedItems" :starting-balance="startingBalance"
                :starting-date="startingDate" class="mt-4" />

            <CashflowDataTable v-else :items="calculatedItems" :loading="loading" @edit="editItem"
                @duplicate="duplicateItem" @delete="deleteItem" />

            <v-divider />
            <v-row class="ma-2 pa-0" v-if="!showCharts">
                <span v-if="calculatedItems">
                    Elementi totali:
                    {{ calculatedItems.length }}
                </span>
            </v-row>

        </v-card>

        <CashflowDialog v-model="dialog" :edited-item="editedItem" :is-new="editedIndex === -1" :saving="saving"
            @save="save" @close="close" />
    </v-container>
</template>

<script>
import * as XLSX from 'xlsx';

import * as api from "@/api/finanza/api.cashflow.js";
import CashflowSettings from "@/components/cashflow/CashflowSettings.vue";
import CashflowDataTable from "@/components/cashflow/CashflowDataTable.vue";
import CashflowDialog from "@/components/cashflow/CashflowDialog.vue";
import CashflowCharts from "@/components/cashflow/CashflowCharts.vue";

// Data di oggi nel fuso orario locale (toISOString userebbe l'UTC e tra mezzanotte e le 2 darebbe il giorno prima)
function oggiLocale() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Campi del record che vengono salvati nel database (esclude i valori calcolati come saldo progressivo e storico)
function campiRecord(item) {
    return {
        id: item.id,
        descrizione: item.descrizione,
        data: item.data,
        orario: item.orario || null,
        tipologia: item.tipologia,
        entrata: item.entrata,
        uscita: item.uscita,
        note: item.note,
        escludiCalcolo: !!item.escludiCalcolo
    };
}

export default {
    name: "CashflowManagement",

    components: {
        CashflowSettings,
        CashflowDataTable,
        CashflowDialog,
        CashflowCharts,
    },

    data: () => ({
        dialog: false,
        loading: false,
        showCharts: false,
        exporting: false,
        saving: false,

        // Saldo e data inizio usati per il calcolo. Si possono cambiare per fare simulazioni: restano locali
        // finché non si preme "Salva", che li scrive nel database per tutti gli utenti.
        startingBalance: 0,
        startingDate: oggiLocale(),
        impostazioniSalvate: { saldo: 0, dataInizio: null },
        savingSettings: false,

        items: [],
        editedIndex: -1,
        editedItem: {
            id: -1,
            descrizione: "",
            data: oggiLocale(),
            orario: null,
            tipologia: "Previsionale",
            entrata: 0,
            uscita: 0,
            note: '',
        },
        message: '',

        history: false,
        historyDate: null,
    }),

    computed: {
        calculatedItems() {
            if (!this.items?.length) return [];

            let dateStart = new Date(this.startingDate);
            dateStart.setHours(0, 0, 0, 0);

            let tipoPriority = {
                Banca: 1,
                Reale: 2,
                Previsionale: 3
            };

            let sorted = [...this.items].sort((a, b) => {
                // 1Ordine per data
                let dateComparison = new Date(a.data) - new Date(b.data);
                if (dateComparison !== 0) return dateComparison;

                // 2Ordine per orario
                let timeA = a.orario || "00:00";
                let timeB = b.orario || "00:00";
                let timeComparison = timeA.localeCompare(timeB);
                if (timeComparison !== 0) return timeComparison;

                // Ordine per tipologia
                return (tipoPriority[a.tipologia] || 99) - (tipoPriority[b.tipologia] || 99);
            });

            let currentTotal = Number(this.startingBalance) || 0;

            // Con "Mostra storico" si vedono anche i movimenti precedenti alla data inizio calcolo (solo informativi).
            // Se la data storico è successiva alla data inizio non si nasconde nulla: i movimenti dopo la data inizio
            // concorrono sempre al saldo.
            let daData = dateStart;
            if (this.history && this.historyDate) {
                const dataStorico = new Date(this.historyDate);
                dataStorico.setHours(0, 0, 0, 0);
                if (dataStorico < daData) daData = dataStorico;
            }

            const risultato = [];
            for (const item of sorted) {
                const dataItem = new Date(item.data);
                if (dataItem < daData) continue;

                const storico = dataItem < dateStart;
                if (!item.escludiCalcolo && !storico)
                    currentTotal += (item.entrata || 0) - (item.uscita || 0);

                risultato.push({
                    ...item,
                    storico,
                    runningTotal: currentTotal.toFixed(3)
                });
            }
            return risultato;
        },

        // Saldo e data inizio diversi da quelli salvati nel database (simulazione in corso)
        impostazioniModificate() {
            return Number(this.startingBalance) !== this.impostazioniSalvate.saldo
                || this.startingDate !== this.impostazioniSalvate.dataInizio;
        }

    },

    created() {
        this.fetchData();
    },

    methods: {
        async fetchData() {
            await Promise.all([
                this.fetchSettings(),
                this.fetchCashflowData()
            ]);
        },

        // Recupera dal database saldo e data inizio calcolo e aggiorna i valori a video (annulla anche eventuali simulazioni)
        async fetchSettings() {
            try {
                const { data } = await api.getCashflowSettings();

                // Se non sono mai state salvate l'API restituisce saldo e data nulli: restano i valori predefiniti
                const saldo = Number(data && data.saldo) || 0;
                const dataInizio = data && data.dataInizio ? data.dataInizio.split("T")[0] : oggiLocale();

                this.startingBalance = saldo;
                this.startingDate = dataInizio;
                this.impostazioniSalvate = { saldo, dataInizio };
            } catch (error) {
                console.error("Errore nel recupero parametri", error);
                this.$toast?.error("Errore nel caricamento delle impostazioni");
            }
        },

        // Scrive saldo e data inizio nel database: da quel momento valgono per tutti gli utenti
        async salvaImpostazioni() {
            const saldo = Number(this.startingBalance);

            if (!this.startingDate || isNaN(saldo)) {
                this.$toast?.alert("Inserire un saldo e una data di inizio validi");
                return;
            }

            const confirm = await this.$confirm?.open(
                "Salva impostazioni",
                "Saldo iniziale e data inizio calcolo verranno salvati per tutti gli utenti. Continuare?"
            );
            if (!confirm) return;

            this.savingSettings = true;
            try {
                await api.saveCashflowSettings({ saldo, dataInizio: this.startingDate });
                this.impostazioniSalvate = { saldo, dataInizio: this.startingDate };
                this.$toast?.success("Impostazioni salvate");
            } catch (error) {
                console.error("Errore nel salvataggio impostazioni", error);
                this.$toast?.error(this.messaggioErrore(error, "Errore durante il salvataggio delle impostazioni"));
            } finally {
                this.savingSettings = false;
            }
        },

        // Messaggio restituito dall'API (es. validazione), altrimenti quello di default
        messaggioErrore(error, predefinito) {
            const dati = error && error.response && error.response.data;
            return typeof dati === "string" && dati ? dati : predefinito;
        },

        async fetchCashflowData() {
            this.loading = true;
            try {
                const { data } = await api.getCashflow();
                this.items = data.map(item => ({
                    ...item,
                    data: item.data.split("T")[0]
                }));
            } catch (error) {
                console.error("Errore nel recupero dati", error);
                this.$toast?.error("Errore nel caricamento dei dati");
            } finally {
                this.loading = false;
            }
        },

        // Un nuovo record parte sempre da id -1: l'id definitivo lo assegna il server
        nuovoRecord() {
            return {
                id: -1,
                descrizione: "",
                data: oggiLocale(),
                orario: null,
                tipologia: "Previsionale",
                entrata: 0,
                uscita: 0,
                note: '',
                escludiCalcolo: false
            };
        },

        openDialog() {
            this.editedItem = this.nuovoRecord();
            this.editedIndex = -1;
            this.dialog = true;
        },

        editItem(item) {
            this.editedIndex = item.id;
            this.editedItem = campiRecord(item);
            this.dialog = true;
        },

        // La copia è un record nuovo: senza azzerare l'id il salvataggio sovrascriverebbe l'originale
        duplicateItem(item) {
            this.editedItem = { ...campiRecord(item), id: -1 };
            this.editedIndex = -1;
            this.dialog = true;
        },

        async deleteItem(item) {
            const confirm = await this.$confirm?.open(
                "Elimina",
                "Eliminare questo record?"
            );

            if (!confirm) return;

            try {
                await api.deleteRecord(item.id);
                this.rimuoviLocale(item.id);
            } catch (error) {
                console.error(error);
                if (error.response && error.response.status === 404) {
                    // già eliminato da un altro utente: allineo la tabella
                    this.$toast?.alert(this.messaggioErrore(error, "Il record non esiste più"));
                    this.rimuoviLocale(item.id);
                    return;
                }
                this.$toast?.error(this.messaggioErrore(error, "Errore durante l'eliminazione"));
            }
        },

        rimuoviLocale(id) {
            const index = this.items.findIndex(x => x.id === id);
            if (index > -1) {
                this.items.splice(index, 1);
            }
        },

        async save(editedItem) {
            if (this.saving) return;

            this.saving = true;
            try {
                const record = campiRecord(editedItem);
                const { data } = await api.saveRecord(record);

                // L'id è sempre quello assegnato dal server: per i nuovi record non lo calcolo più qui
                const salvato = { ...record, id: data.id };
                const index = this.items.findIndex(x => x.id === salvato.id);
                if (index > -1) {
                    this.$set(this.items, index, { ...this.items[index], ...salvato });
                } else {
                    this.items.push(salvato);
                }
                this.close();
            } catch (error) {
                console.error(error);
                if (error.response && error.response.status === 404) {
                    // il record modificato è stato eliminato da un altro utente: ricarico i dati
                    this.$toast?.alert(this.messaggioErrore(error, "Il record non esiste più"));
                    this.close();
                    this.fetchCashflowData();
                    return;
                }
                this.$toast?.error(this.messaggioErrore(error, "Errore durante il salvataggio"));
            } finally {
                this.saving = false;
            }
        },

        close() {
            this.dialog = false;
            this.$nextTick(() => {
                this.editedItem = this.nuovoRecord();
                this.editedIndex = -1;
            });
        },

        async importaDati() {
            let res = await api.importData();
            this.message = res.data;
        },

        //#region  Esporta Excel

        async exportToExcel() {
            this.exporting = true;
            try {
                // Prepara i dati per l'export
                const exportData = this.calculatedItems.map(item => ({
                    'Data': this.formatDateForExport(item.data),
                    'Orario': item.orario || '',
                    'Descrizione': item.descrizione,
                    'Tipo': item.tipologia,
                    'Entrate (€)': item.entrata || 0,
                    'Uscite (€)': item.uscita || 0,
                    'Saldo Progressivo (€)': item.runningTotal
                }));

                // Calcola totali
                const totaleEntrate = exportData.reduce((sum, item) => sum + item['Entrate (€)'], 0);
                const totaleUscite = exportData.reduce((sum, item) => sum + item['Uscite (€)'], 0);
                const saldoFinale = exportData.length > 0 ? exportData[exportData.length - 1]['Saldo Progressivo (€)'] : 0;

                // Aggiungi riga vuota e riga di riepilogo
                exportData.push({});
                exportData.push({
                    'Data': 'TOTALE',
                    'Orario': '',
                    'Descrizione': '',
                    'Tipo': '',
                    'Entrate (€)': totaleEntrate,
                    'Uscite (€)': totaleUscite,
                    'Saldo Progressivo (€)': saldoFinale
                });

                // Aggiungi informazioni iniziali
                exportData.unshift({});
                exportData.unshift({
                    'Data': 'Saldo Iniziale',
                    'Orario': '',
                    'Descrizione': '',
                    'Tipo': '',
                    'Entrate (€)': '',
                    'Uscite (€)': '',
                    'Saldo Progressivo (€)': this.startingBalance
                });
                exportData.unshift({
                    'Data': 'Data Inizio',
                    'Orario': '',
                    'Descrizione': this.formatDateForExport(this.startingDate),
                    'Tipo': '',
                    'Entrate (€)': '',
                    'Uscite (€)': '',
                    'Saldo Progressivo (€)': ''
                });
                exportData.unshift({});

                // Crea workbook
                const ws = XLSX.utils.json_to_sheet(exportData, {
                    skipHeader: false
                });

                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Cashflow");

                // Imposta larghezza colonne
                ws['!cols'] = [
                    { wch: 12 }, // Data
                    { wch: 8 },  // Orario
                    { wch: 30 }, // Descrizione
                    { wch: 15 }, // Tipo
                    { wch: 14 }, // Entrate
                    { wch: 14 }, // Uscite
                    { wch: 20 }  // Saldo Progressivo
                ];

                // Genera file
                const fileName = `Cashflow_${this.formatDateForFilename(new Date())}.xlsx`;
                XLSX.writeFile(wb, fileName);

            } catch (error) {
                console.error('Errore durante l\'esportazione', error);
                this.$toast?.error('Errore durante l\'esportazione Excel');
            } finally {
                this.exporting = false;
            }
        },

        formatDateForExport(isoDate) {
            if (!isoDate) return '';
            const [year, month, day] = isoDate.split('-');
            return `${day}/${month}/${year}`;
        },

        formatDateForFilename(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}${month}${day}_${hours}${minutes}`;
        },

        //#endregion
    }
};
</script>